/**
 * Структурный парсер venue.json: проверяет всё, на что опирается вьюер
 * (обязательные поля, форму элементов, ссылочную целостность). Контракт:
 * невалидный вход → RvfError со списком проблем, никаких голых TypeError.
 * Полная JSON Schema — задача B2B-валидатора (docs/BACKLOG.md).
 */
import { isServiceKind, LAYER_OF_KIND } from './service-layer'
import type { ServicePoint, Venue } from './types'

export class RvfError extends Error {
  readonly issues: readonly string[]

  constructor(issues: readonly string[]) {
    super(`Invalid RVF: ${issues.join('; ')}`)
    this.name = 'RvfError'
    this.issues = issues
  }
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

/** [lon, lat]: конечные числа в допустимых диапазонах (NaN не проходит). */
const isLonLat = (v: unknown): boolean =>
  Array.isArray(v) &&
  v.length === 2 &&
  Number.isFinite(v[0]) &&
  Number.isFinite(v[1]) &&
  Math.abs(v[0] as number) <= 180 &&
  Math.abs(v[1] as number) <= 90

const isBbox = (v: unknown): boolean =>
  Array.isArray(v) && v.length === 4 && v.every((n) => Number.isFinite(n))

function checkMeta(root: Record<string, unknown>, issues: string[]): void {
  if (typeof root['rvf'] !== 'string') issues.push('rvf: version string required')
  const meta = root['venue']
  if (!isObj(meta)) {
    issues.push('venue: object required')
    return
  }
  if (typeof meta['id'] !== 'string' || !/^[\w-]+$/.test(meta['id'] as string)) {
    issues.push('venue.id: [\\w-]+ required')
  }
  if (!isObj(meta['name'])) issues.push('venue.name: localized object required')
  if (!isLonLat(meta['center'])) issues.push('venue.center: [lon, lat] required')
  if (!isBbox(meta['bbox'])) issues.push('venue.bbox: [w, s, e, n] of finite numbers required')
  if (typeof meta['source'] !== 'string') issues.push('venue.source: required (ODbL-гигиена)')
}

/** Цвет заливки: только `#RRGGBB` — тот вид, что понимает и карта, и редактор. */
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value)
}

/** Массивы секций + каждый элемент — объект (иначе дальше были бы TypeError). */
function checkCollections(root: Record<string, unknown>, issues: string[]): void {
  const collections: Array<[string, unknown]> = [
    ['levels', root['levels']],
    ['units', root['units']],
    ['pois', root['pois']],
    ['anchors', root['anchors']],
  ]
  const network = root['network']
  if (isObj(network)) {
    collections.push(['network.nodes', network['nodes']], ['network.edges', network['edges']])
  } else {
    issues.push('network: object required')
  }
  for (const [name, value] of collections) {
    if (!Array.isArray(value)) {
      issues.push(`${name}: array required`)
      continue
    }
    value.forEach((item, i) => {
      if (!isObj(item)) issues.push(`${name}[${i}]: object required`)
    })
  }
}

function checkNodes(venue: Venue, issues: string[]): void {
  const levelIds = new Set(venue.levels.map((l) => l.id))
  const seen = new Set<string>()
  for (const node of venue.network.nodes) {
    if (seen.has(node.id)) issues.push(`node ${node.id}: duplicate id`)
    seen.add(node.id)
    if (!isLonLat(node.point)) issues.push(`node ${node.id}: point [lon, lat] required`)
    if (!levelIds.has(node.level)) issues.push(`node ${node.id}: unknown level ${node.level}`)
  }
}

function checkEdges(venue: Venue, nodeIds: ReadonlySet<string>, issues: string[]): void {
  for (const edge of venue.network.edges) {
    if (!nodeIds.has(edge.from)) issues.push(`edge: unknown node ${edge.from}`)
    if (!nodeIds.has(edge.to)) issues.push(`edge: unknown node ${edge.to}`)
  }
}

/** POI/якоря/юниты: геометрия, на которую опираются карта и маршрутизация. */
function checkGeometry(venue: Venue, issues: string[]): void {
  for (const poi of venue.pois) {
    if (!isLonLat(poi.point)) issues.push(`poi ${poi.id}: point [lon, lat] required`)
  }
  for (const anchor of venue.anchors) {
    if (!isLonLat(anchor.point)) issues.push(`anchor ${anchor.id}: point [lon, lat] required`)
  }
  for (const unit of venue.units) {
    const geometry = unit.geometry as unknown
    if (
      !isObj(geometry) ||
      geometry['type'] !== 'Polygon' ||
      !Array.isArray(geometry['coordinates'])
    ) {
      issues.push(`unit ${unit.id}: geometry Polygon required`)
    }
    // Цвет проверяем строго: строка «синий» или «rgb(…)» доедет до слоя карты
    // и молча покрасит объект в чёрный — MapLibre не жалуется, просто рисует
    // не то. Ошибка на публикации дешевле такой находки на площадке.
    if (unit.color !== undefined && !isHexColor(unit.color)) {
      issues.push(`unit ${unit.id}: color must be #RRGGBB`)
    }
  }
}

/**
 * Вид и слой служебной точки (ADR 0021). Слой не принимается из данных на веру,
 * а сверяется с реестром: точка, попавшая не в свой слой, будет показана не той
 * службе — это хуже, чем не показана никому.
 */
function checkServiceKind(point: ServicePoint, issues: string[]): void {
  if (!isServiceKind(point.kind)) {
    issues.push(`service ${point.id}: unknown kind ${String(point.kind)}`)
    return
  }
  const expected = LAYER_OF_KIND[point.kind]
  if (point.layer !== expected) {
    issues.push(
      `service ${point.id}: kind ${point.kind} belongs to layer ${expected}, not ${String(point.layer)}`,
    )
  }
}

/** Геометрия и ссылки служебной точки — те же правила, что у прочих секций. */
function checkServiceRefs(
  point: ServicePoint,
  levelIds: ReadonlySet<string>,
  nodeIds: ReadonlySet<string>,
  issues: string[],
): void {
  if (!isLonLat(point.point)) issues.push(`service ${point.id}: point [lon, lat] required`)
  if (!levelIds.has(point.level)) issues.push(`service ${point.id}: unknown level ${point.level}`)
  if (point.node !== undefined && !nodeIds.has(point.node)) {
    issues.push(`service ${point.id}: unknown node ${point.node}`)
  }
  if (!isObj(point.name)) issues.push(`service ${point.id}: localized name required`)
}

function checkService(venue: Venue, nodeIds: ReadonlySet<string>, issues: string[]): void {
  const raw = venue.service as unknown
  // Секция необязательна, но если она есть — обязана быть массивом. Без этой
  // проверки объект вместо массива давал бы голый TypeError вместо RvfError,
  // а контракт модуля обещает список проблем (см. заголовок файла).
  if (raw === undefined) return
  if (!Array.isArray(raw)) {
    issues.push('service: array required')
    return
  }
  const levelIds = new Set(venue.levels.map((l) => l.id))
  const seen = new Set<string>()
  for (const point of venue.service ?? []) {
    if (!isObj(point)) {
      issues.push('service: object required')
      continue
    }
    if (typeof point.id !== 'string' || point.id === '') {
      issues.push('service: id required')
      continue
    }
    if (seen.has(point.id)) issues.push(`service ${point.id}: duplicate id`)
    seen.add(point.id)
    checkServiceKind(point, issues)
    checkServiceRefs(point, levelIds, nodeIds, issues)
  }
}

/**
 * Ритейл: товары ссылаются на существующие полки, а коды мест (attrs.code)
 * уникальны в пределах площадки — код диктуют голосом и печатают на ценнике,
 * два места с одним кодом означают «маршрут приведёт не туда».
 */
function checkProducts(venue: Venue, issues: string[]): void {
  const raw = (venue as { products?: unknown }).products
  if (raw !== undefined && !Array.isArray(raw)) {
    issues.push('products: array required')
    return
  }
  const poiIds = new Set(venue.pois.map((p) => p.id))
  const seen = new Set<string>()
  for (const product of venue.products ?? []) {
    if (!isObj(product) || typeof product.id !== 'string' || product.id === '') {
      issues.push('products: object with id required')
      continue
    }
    if (seen.has(product.id)) issues.push(`product ${product.id}: duplicate id`)
    seen.add(product.id)
    if (!isObj(product.name)) issues.push(`product ${product.id}: localized name required`)
    if (typeof product.shelf !== 'string' || !poiIds.has(product.shelf)) {
      issues.push(`product ${product.id}: unknown shelf ${String(product.shelf)}`)
    }
  }
}

/** Коды мест уникальны: код диктуют голосом, дубль ведёт «не туда». */
function checkShelfCodes(venue: Venue, issues: string[]): void {
  const codes = new Map<string, string>()
  for (const poi of venue.pois) {
    const code = poi.attrs?.['code']
    if (typeof code !== 'string' || code.trim() === '') continue
    const norm = code.replace(/\s+/g, '').toUpperCase()
    const owner = codes.get(norm)
    if (owner !== undefined) issues.push(`poi ${poi.id}: duplicate code ${code} (also ${owner})`)
    codes.set(norm, poi.id)
  }
}

function checkNodeRefs(venue: Venue, nodeIds: ReadonlySet<string>, issues: string[]): void {
  for (const unit of venue.units) {
    for (const entrance of unit.entrances ?? []) {
      if (!nodeIds.has(entrance)) issues.push(`unit ${unit.id}: unknown entrance ${entrance}`)
    }
  }
  for (const anchor of venue.anchors) {
    if (anchor.node !== undefined && !nodeIds.has(anchor.node)) {
      issues.push(`anchor ${anchor.id}: unknown node ${anchor.node}`)
    }
  }
}

/** Разбор и структурная валидация. Бросает RvfError со списком проблем. */
export function parseVenue(data: unknown): Venue {
  const issues: string[] = []
  if (!isObj(data)) throw new RvfError(['root: object required'])
  checkMeta(data, issues)
  checkCollections(data, issues)
  if (issues.length > 0) throw new RvfError(issues)
  const venue = data as unknown as Venue
  const nodeIds = new Set(venue.network.nodes.map((n) => n.id))
  checkNodes(venue, issues)
  checkEdges(venue, nodeIds, issues)
  checkGeometry(venue, issues)
  checkNodeRefs(venue, nodeIds, issues)
  checkService(venue, nodeIds, issues)
  checkProducts(venue, issues)
  checkShelfCodes(venue, issues)
  if (issues.length > 0) throw new RvfError(issues)
  return withDefaults(venue)
}

/** Выводит profiles=[type] при отсутствии (обратная совместимость, ADR 0007). */
function withDefaults(venue: Venue): Venue {
  if (venue.venue.profiles && venue.venue.profiles.length > 0) return venue
  return { ...venue, venue: { ...venue.venue, profiles: [venue.venue.type] } }
}
